// Maps DealerForm state onto the UXCam session so every recording carries the
// who / what / how of the transaction being worked on.
//
// Session-long facts (who is being renewed, which discounts are in play) are
// user properties; discrete decisions are events, so they can be sequenced on
// the replay timeline and used to build funnels.
import { setUxcamProperties, trackUxcamEvent } from "@/services/uxcam";
// Several of these flags are stored as the strings "true"/"false".
import { truthyBoolean as truthy } from "@/utils/truthyBoolean";

export default {
  computed: {
    // One object so a single watcher covers every field. Vue re-evaluates this
    // whenever any dependency changes, and the watcher diffs against what was
    // already sent, so unchanged keys are not re-transmitted.
    //
    // Deliberately narrow: UXCam allows 20 user properties per user in total,
    // shared with `environment`, `agent_name` and the four issue/error
    // counters. Anything that is a fact about one moment rather than about the
    // whole session belongs on an event below, where the cap does not apply.
    uxcamContext() {
      const promoCode = this.promotion_code || null;

      return {
        flow_type: this.isRenew ? "Renew" : "New",
        is_trial: !!this.CardSelection?.includes("Trial"),
        membership_card: this.CardSelection || null,

        // Who is being renewed / signed up.
        member_name: this.account?.name || null,
        member_number: this.membership_number__c || null,
        contact_sfid: this.contact_sfid || null,
        membership_sfid: this.membership_sfid || null,

        // Discounts and attribution.
        used_promo_code: !!promoCode,
        promo_code: promoCode,
        used_referring_account: !!this.referring_account,

        // Payment shape.
        arb_enabled: truthy(this.arb_value),
        funds_collected_locally: truthy(this.funds_collected_locally),
      };
    },
  },

  watch: {
    uxcamContext: {
      immediate: true,
      handler(context) {
        setUxcamProperties(context);
      },
    },

    // A membership_sfid appearing means a member's record was pulled into the
    // form - the moment the session stops being generic and starts being about
    // a specific person.
    membership_sfid(sfid) {
      if (!sfid) return;
      trackUxcamEvent("member_loaded", {
        membership_sfid: sfid,
        account_sfid: this.account_sfid,
        contact_sfid: this.contact_sfid,
        boat_sfid: this.boat_sfid,
        member_number: this.membership_number__c,
        member_name: this.account?.name,
        member_status: this.membership_status__c,
        aor: this.aor_display_field,
        already_enrolled_in_arb: this.isAlreadyEnrolledInArb === true,
      });
    },

    arb_value(value) {
      trackUxcamEvent("arb_toggled", {
        enabled: truthy(value),
        required_by_promo: truthy(this.promotion_arb_required),
      });
    },

    funds_collected_locally(value) {
      trackUxcamEvent("funds_collected_locally_toggled", {
        enabled: truthy(value),
      });
    },

    referring_account(sfid) {
      if (!sfid) return;
      trackUxcamEvent("referring_account_selected", {
        referring_account_sfid: sfid,
      });
    },

    promotion_valid(valid) {
      if (!valid) return;
      trackUxcamEvent("promo_code_applied", {
        promo_code: this.promotion_code,
        promo_type: this.promotion_type__c,
        campaign_sfid: this.campaign_sfid,
        arb_required: truthy(this.promotion_arb_required),
      });
    },

    CardSelection(card) {
      trackUxcamEvent("membership_card_selected", { card });
    },
  },

  methods: {
    // Passthrough so components can log an event without importing the service.
    trackUxcamEvent(name, properties) {
      trackUxcamEvent(name, properties);
    },

    //
    // Submission funnel. `status` is one of started / blocked / succeeded /
    // failed so a single event name carries the whole outcome distribution.
    //
    trackUxcamSubmit(status, extra) {
      trackUxcamEvent("membership_submit", {
        status,
        flow_type: this.isRenew ? "Renew" : "New",
        membership_card: this.CardSelection,
        price_total: this.price_total,
        arb_enabled: truthy(this.arb_value),
        funds_collected_locally: truthy(this.funds_collected_locally),
        used_promo_code: !!this.promotion_code,
        used_activation_code: !!this.activation_codes?.name,
        activation_code: this.activation_codes?.name,
        referring_account_sfid: this.referring_account,
        arb_enroll_only: truthy(this.arb_enroll_only),
        trailer_care: this.TrailerSelection,
        donation_amount: Number(this.donation_amount) || 0,
        member_number: this.membership_number__c,
        membership_sfid: this.membership_sfid,
        ...(extra || {}),
      });
    },
  },
};
